// Wires the selection-driven bulk actions, keyboard shortcuts, inline edit
// and confirm dialog for the v2 admin enquiry moderation page.

export default function initAdminEnquiryModeration () {
  const page = document.querySelector('.vjs-em-page')
  if (!page) return

  page.addEventListener('change', handleSelectChange)
  page.addEventListener('click', handlePageClick)
  page.addEventListener('submit', handleEditFormSubmit)

  document.querySelectorAll('.vjs-em-select-all').forEach((btn) => {
    btn.addEventListener('click', () => setAllSelected(true))
  })
  document.querySelectorAll('.vjs-em-deselect-all').forEach((btn) => {
    btn.addEventListener('click', () => setAllSelected(false))
  })

  document.querySelectorAll('.vjs-em-sort-toggle').forEach((btn) => {
    btn.addEventListener('click', handleSortToggle)
  })

  document.addEventListener('keydown', handleKeydown)

  window.vjsEmConfirmBulk = confirmBulk

  refreshFooter()
}

// --- Selection / footer state -----------------------------------------------

function refreshFooter () {
  const checkboxes = Array.from(document.querySelectorAll('.vjs-em-select'))
  const count = checkboxes.filter((c) => c.checked).length

  const selectionCount = document.querySelector('.vjs-em-selection-count')
  if (selectionCount) selectionCount.textContent = String(count)

  const approveBtn = document.querySelector('.vjs-em-bulk-approve')
  const rejectBtn = document.querySelector('.vjs-em-bulk-reject')
  if (approveBtn) approveBtn.disabled = count === 0
  if (rejectBtn) rejectBtn.disabled = count === 0

  const footer = document.querySelector('.vjs-em-footer')
  if (footer) {
    footer.hidden = count === 0
    footer.dataset.state = count > 0 ? 'active' : 'idle'
  }

  checkboxes.forEach(setBubbleSelectionState)
}

function setBubbleSelectionState (cb) {
  const bubble = cb.closest('.vjs-em-bubble')
  if (bubble) bubble.classList.toggle('vsd-admin-em__bubble--selected', cb.checked)
}

function setAllSelected (checked) {
  document.querySelectorAll('.vjs-em-select').forEach((cb) => { cb.checked = checked })
  refreshFooter()
}

// --- Sort order toggle -------------------------------------------------------

// Flips the thread between oldest-first (default) and newest-first. The DOM is
// reordered live for instant feedback, and the choice is persisted to the
// session so it sticks across enquiries for this moderator.
function handleSortToggle (e) {
  const btn = e.currentTarget
  const current = btn.dataset.sortDirection === 'newest' ? 'newest' : 'oldest'
  const next = current === 'newest' ? 'oldest' : 'newest'

  applySortDirection(next)
  btn.dataset.sortDirection = next
  btn.textContent = next === 'newest' ? 'Sort Oldest' : 'Sort Newest'
  persistSortPreference(btn.dataset.sortUrl, next)
}

function applySortDirection (direction) {
  const thread = document.querySelector('.vsd-admin-em__thread')
  if (!thread) return

  // Comment units are the bubbles plus the activity-log dividers; the plain
  // date dividers are layout-only and get rebuilt once the units are reordered.
  const units = Array.from(thread.children).filter(isCommentUnit)
  units.sort((a, b) => Number(a.dataset.commentId) - Number(b.dataset.commentId)) // chronological
  if (direction === 'newest') units.reverse()

  thread.querySelectorAll(':scope > .vsd-admin-em__divider:not([data-comment-id])')
    .forEach((divider) => divider.remove())

  let lastDay = null
  units.forEach((unit) => {
    const day = unit.dataset.day
    if (day && day !== lastDay) {
      thread.appendChild(buildDateDivider(unit.dataset.dayLabel))
      lastDay = day
    }
    thread.appendChild(unit)
  })
}

function isCommentUnit (el) {
  if (el.classList.contains('vsd-admin-em__bubble-wrap')) return true
  return el.classList.contains('vsd-admin-em__divider') && el.hasAttribute('data-comment-id')
}

function buildDateDivider (label) {
  const divider = document.createElement('div')
  divider.className = 'vsd-admin-em__divider'
  divider.textContent = label || ''
  return divider
}

function persistSortPreference (url, direction) {
  if (!url) return
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''
  window.fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({ direction }),
    credentials: 'same-origin'
  }).catch(() => {}) // best-effort: the thread is already reordered locally
}

// --- Click routing -----------------------------------------------------------

function handleSelectChange (e) {
  if (e.target.classList && e.target.classList.contains('vjs-em-select')) refreshFooter()
}

function handlePageClick (e) {
  if (handleEditOpenClick(e)) return
  if (handleEditCancelClick(e)) return
  if (handleRevertToggleClick(e)) return
  handleBubbleToggleClick(e)
}

function handleBubbleToggleClick (e) {
  const bubble = e.target.closest('.vjs-em-bubble-pending')
  if (!bubble) return
  if (clickIsOnInteractiveTarget(e.target)) return

  const cb = bubble.querySelector('.vjs-em-select')
  if (!cb) return
  cb.checked = !cb.checked
  refreshFooter()
}

function clickIsOnInteractiveTarget (target) {
  return Boolean(target.closest(
    '.vjs-em-bubble-actions, ' +
    '.vjs-em-bubble-edit, ' +
    'button, a, input, textarea, label, select, form'
  ))
}

// --- Revert previous decision --------------------------------------------------

// The pencil toggle on already-moderated bubbles shows/hides the revert
// actions row (approve / redact / reject).
function handleRevertToggleClick (e) {
  const toggle = e.target.closest('.vjs-em-revert-toggle')
  if (!toggle) return false
  e.preventDefault()

  const bubble = toggle.closest('.vjs-em-bubble')
  const actions = bubble?.querySelector('.vjs-em-revert-actions')
  if (actions) {
    actions.hidden = !actions.hidden
    toggle.classList.toggle('vsd-admin-em__revert-toggle--open', !actions.hidden)
  }
  return true
}

// --- Inline edit -------------------------------------------------------------

function handleEditOpenClick (e) {
  const editBtn = e.target.closest('.vjs-em-edit')
  if (!editBtn || !editBtn.dataset.editTarget) return false
  e.preventDefault()
  setEditMode(editBtn.closest('.vjs-em-bubble'), true)
  return true
}

function handleEditCancelClick (e) {
  const cancelBtn = e.target.closest('.vjs-em-edit-cancel')
  if (!cancelBtn) return false
  e.preventDefault()
  setEditMode(cancelBtn.closest('.vjs-em-bubble'), false)
  return true
}

function setEditMode (bubble, editing) {
  if (!bubble) return
  const content = bubble.querySelector('.vjs-em-bubble-content')
  const actions = bubble.querySelector('.vjs-em-bubble-actions')
  const editShell = bubble.querySelector('.vjs-em-edit-shell')
  if (content) content.hidden = editing
  if (actions) actions.hidden = editing
  if (editShell) toggleEditShell(editShell, editing)
}

function toggleEditShell (editShell, editing) {
  editShell.hidden = !editing
  if (!editing) return
  const ta = editShell.querySelector('textarea')
  if (!ta) return
  ta.focus()
  ta.setSelectionRange(ta.value.length, ta.value.length)
}

function handleEditFormSubmit (e) {
  const form = e.target.closest('form[data-vjs-em-edit-form]')
  if (!form) return
  e.preventDefault()
  submitEditForm(form)
}

function submitEditForm (form) {
  const bubble = form.closest('.vjs-em-bubble')
  const saveBtn = form.querySelector('button[type="submit"]')
  if (saveBtn) saveBtn.disabled = true

  const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''

  window.fetch(form.action, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrf,
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: new FormData(form),
    credentials: 'same-origin'
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    })
    .then((json) => applyEditResponse(bubble, json))
    .catch((err) => {
      // eslint-disable-next-line no-alert
      window.alert(`Could not save edit: ${err.message}`)
    })
    .finally(() => { if (saveBtn) saveBtn.disabled = false })
}

function applyEditResponse (bubble, json) {
  const content = bubble.querySelector('.vjs-em-bubble-content')
  replaceBubbleBody(content, json.content)
  ensureEditedNotice(content, json.edited_notice)
  ensureOriginalContent(content, json.original_content)
  setEditMode(bubble, false)
}

function replaceBubbleBody (content, text) {
  const body = content?.querySelector('.vjs-em-bubble-body')
  if (!body) return
  body.innerHTML = text.split(/\n+/).map((p) => `<p>${escapeHtml(p)}</p>`).join('')
}

function ensureEditedNotice (content, text) {
  if (!content || content.querySelector('[data-edited-notice="admin"]')) return
  const p = document.createElement('p')
  p.className = 'vsd-admin-em__bubble-edited-notice'
  p.dataset.editedNotice = 'admin'
  p.textContent = text
  content.appendChild(p)
}

// Admin-only context block showing the sender's original message before it was
// redacted. Built (or refreshed) to mirror the server-rendered markup so a
// just-redacted bubble matches what other admins see on reload.
function ensureOriginalContent (content, text) {
  if (!content || !text) return
  let block = content.querySelector('[data-original-content]')
  if (!block) {
    block = document.createElement('div')
    block.className = 'vsd-admin-em__bubble-original'
    block.dataset.originalContent = ''
    const label = document.createElement('span')
    label.className = 'vsd-admin-em__bubble-original-label'
    label.textContent = 'Original message (before redaction)'
    const body = document.createElement('div')
    body.className = 'vsd-admin-em__bubble-original-body'
    block.appendChild(label)
    block.appendChild(body)
    content.appendChild(block)
  }
  const body = block.querySelector('.vsd-admin-em__bubble-original-body')
  if (body) body.innerHTML = text.split(/\n+/).map((p) => `<p>${escapeHtml(p)}</p>`).join('')
}

// --- Bulk confirm dialog -----------------------------------------------------

function confirmBulk (form, action) {
  const ids = Array.from(document.querySelectorAll('.vjs-em-select'))
    .filter((c) => c.checked)
    .map((c) => c.value)

  if (ids.length === 0) {
    window.alert('Select at least one pending comment first.')
    return false
  }
  const verb = action === 'approve' ? 'Approve' : 'Reject'
  if (!window.confirm(`${verb} ${ids.length} comment(s)?`)) return false

  form.querySelectorAll('input[name="comment_ids[]"]').forEach((i) => i.remove())
  ids.forEach((id) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'comment_ids[]'
    input.value = id
    form.appendChild(input)
  })
  return true
}

// --- Keyboard shortcuts ------------------------------------------------------

function handleKeydown (e) {
  if (e.target.matches('input, textarea, select')) return
  if (e.metaKey || e.ctrlKey || e.altKey) return

  // ⇧+A — toggle: select all if any are unselected, otherwise clear.
  if (e.shiftKey && (e.key === 'A' || e.key === 'a')) {
    e.preventDefault()
    toggleAllSelection()
    return
  }
  if (e.shiftKey) return
  runShortcutForKey(e.key)
}

function toggleAllSelection () {
  const all = Array.from(document.querySelectorAll('.vjs-em-select'))
  const allChecked = all.length > 0 && all.every((c) => c.checked)
  setAllSelected(!allChecked)
}

function runShortcutForKey (key) {
  switch (key) {
    case 'j': return navigateToPrev()
    case 'k': return navigateToNext()
    case 'a': return submitBulkForm('vjs-em-bulk-approve', '.vjs-em-bulk-approve')
    case 'r': return submitBulkForm('vjs-em-bulk-reject', '.vjs-em-bulk-reject')
  }
}

function navigateToPrev () {
  const prevId = document.querySelector('.vjs-em-page')?.dataset.prevId
  if (prevId) window.location.href = `/admin/enquiry_moderation/${prevId}`
}

function navigateToNext () {
  const nextId = document.querySelector('.vjs-em-page')?.dataset.nextId
  if (nextId) window.location.href = `/admin/enquiry_moderation/${nextId}`
}

function submitBulkForm (formId, btnSelector) {
  const form = document.getElementById(formId)
  const btn = document.querySelector(btnSelector)
  if (form && btn && !btn.disabled) form.requestSubmit()
}

// --- Utilities ---------------------------------------------------------------

function escapeHtml (s) {
  return String(s).replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ))
}
